import { Suspense, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { EntityPageFocusType } from '@xing-com/crate-common-graphql-types';
import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import {
  ErrorState,
  useAboutQuerySuspense,
} from '@xing-com/crate-entity-pages-about-reduced';
import {
  EP_NwtEditViewAction,
  useEntityPageNwtEditViewAction,
} from '@xing-com/crate-entity-pages-common';
import { useEditStore } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { IconEdit } from '@xing-com/icons';
import { SkeletonBodyCopy } from '@xing-com/skeleton';
import { BodyCopy, Hero } from '@xing-com/typography';

import * as Styled from './about-content-pages-module.styles';

const AboutContentPagesModuleInner: React.FC = () => {
  const { pageContext } = usePageContext();
  const { failed, loading, refetch, summary } = useAboutQuerySuspense(
    pageContext.pageSlug
  );

  if (failed) return <ErrorState loading={loading} refetch={refetch} />;

  return <BodyCopy size="medium">{summary}</BodyCopy>;
};

const Fallback: React.FC = () => {
  return (
    <SkeletonBodyCopy
      alignment="left"
      amountRows={4}
      data-qa="about-visitor-view-skeleton"
    />
  );
};

export const AboutContentPagesModule: React.FC = () => {
  const isEditing = useEditStore(({ isEditing }) => isEditing);
  const { pageContext } = usePageContext();
  const { trackEntityPageEditView } = useEntityPageNwtEditViewAction();

  const titleCopy = useMemo(() => {
    switch (pageContext.focusType) {
      case EntityPageFocusType.Insider:
        return 'EP_ABOUT_VISITOR_VIEW_TITLE_INSIDER';
      case EntityPageFocusType.Publisher:
        return 'EP_ABOUT_VISITOR_VIEW_TITLE_PUBLISHER';
      default:
        return 'EP_ABOUT_VISITOR_VIEW_TITLE';
    }
  }, [pageContext.focusType]);

  return (
    <Styled.AboutVisitorViewContainer>
      <Styled.AboutVisitorHeadlineContainer>
        <Hero size="small" data-testid="module-header-title">
          <FormattedMessage id={titleCopy} />
        </Hero>
        {isEditing ? (
          <Styled.AboutVisitorEditButton
            icon={IconEdit}
            size="small"
            to={`/pages/${pageContext.pageSlug}/edit/aboutEditView?type=short`}
            onClick={() =>
              trackEntityPageEditView(
                EP_NwtEditViewAction.ABOUT_DESCRIPTION_OPEN
              )
            }
          />
        ) : null}
      </Styled.AboutVisitorHeadlineContainer>
      <ErrorBoundary
        onError={() => (
          <ErrorState
            loading={false}
            refetch={() => window.location.reload()}
          />
        )}
      >
        <Suspense fallback={<Fallback />}>
          <AboutContentPagesModuleInner />
        </Suspense>
      </ErrorBoundary>
    </Styled.AboutVisitorViewContainer>
  );
};
